import { useEffect, useState } from "react";
import { ArrowRight, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useTipo } from "@/lib/segmento";
import { MemoryModal } from "@/components/MemoryModal";
import { BrainCircuit } from "lucide-react";
import { imparaDa, useMemoria } from "@/lib/memoria";
import { Button } from "@/components/ui/button";

const COPY = {
  it: {
    badge: "Demo live, senza registrazione",
    title: "Provalo adesso con un lavoro tuo.",
    sub: "Scrivi un lavoro che fai davvero e guarda cosa esce. Nessuna registrazione.",
    placeholder: "es. Rifacimento impianto elettrico cucina di un ristorante, 3 giorni, materiali inclusi",
    generating: "Sto scrivendo il preventivo…",
    generate: "Genera il preventivo",
    quote: "Preventivo",
    subtotal: "Subtotale",
    tax: "IVA",
    pdfPitch: "Lo vuoi in PDF? Ti scriviamo al lancio e ti riserviamo",
    pdfBonus: "+20 azioni AI al mese, per sempre",
    emailPlaceholder: "tu@azienda.it",
    getPdf: "Scarica il PDF",
    footer: "Nell'app il PDF porta il tuo logo, i dati della tua azienda e il tuo IBAN, e parte al cliente per email con un clic.",
    createAccount: "Crea il tuo account",
    pdfDone: "PDF scaricato. Sei nella lista di lancio con +20 azioni AI al mese.",
    pdfError: "Non siamo riusciti a creare il PDF. Riprova.",
    limit: "Hai raggiunto le prove gratuite di oggi. Registrati per continuare senza limiti.",
    examples: [
      "Ristrutturazione bagno 6 mq per un cliente a Milano, 8 giorni di lavoro, sanitari di media gamma",
      "Restyling sito web, 8 pagine, analisi UX, configurazione CMS e 12 mesi di hosting",
      "Rifacimento illuminazione LED di un magazzino, 46 corpi illuminanti, certificazione inclusa",
    ],
  },
  en: {
    badge: "Live demo, no signup",
    title: "Now try it with one of your own jobs.",
    sub: "Type a job you actually do and see what comes out. No signup.",
    placeholder: "e.g. Kitchen rewiring for a restaurant, 3 days, materials included",
    generating: "Writing your quote…",
    generate: "Generate the quote",
    quote: "Quote",
    subtotal: "Subtotal",
    tax: "VAT",
    pdfPitch: "Want it as a PDF? We'll email you at launch and reserve",
    pdfBonus: "+20 AI actions a month, for life",
    emailPlaceholder: "you@company.com",
    getPdf: "Get the PDF",
    footer: "In the app the PDF carries your logo, your company details and your bank details, and goes to the client by email in one click.",
    createAccount: "Create your account",
    pdfDone: "PDF downloaded. You're on the launch list with +20 AI actions a month.",
    pdfError: "We couldn't build the PDF. Please try again.",
    limit: "You've used today's free demos. Create an account to keep going.",
    examples: [
      "Bathroom refit 6 sqm for a client in Manchester, 8 working days, mid-range fixtures",
      "Website redesign, 8 pages, UX audit, CMS setup and 12 months hosting",
      "Warehouse LED lighting upgrade, 46 fixtures, electrical certification included",
    ],
  },
};

// Esempi per tipo di professionista: arrivano dal selettore sopra la demo.
const PER_TIPO = {
  it: {
    artigiano: "Posa piastrelle, PVC Deceuninck, 450€/mq + 10% sfrido",
    freelance: "Giornata consulenza 450€ + acconto 40%",
    avvocato: "Parere scritto forfettario + spese",
    pmi: "Fornitura + posa + assistenza",
  },
  en: {
    artigiano: "Tile laying, Deceuninck PVC, 450/sqm + 10% waste",
    freelance: "Consulting day 450 + 40% deposit",
    avvocato: "Written legal opinion, flat fee + expenses",
    pmi: "Supply + installation + support",
  },
};

export const DemoQuote = () => {
  const { lang } = useI18n();
  const base = COPY[lang] || COPY.en;
  const tipo = useTipo();
  const suGiu = PER_TIPO[lang]?.[tipo] || PER_TIPO.en[tipo];
  const c = suGiu ? { ...base, placeholder: suGiu, examples: [suGiu] } : base;
  const [brief, setBrief] = useState("");
  const [toccato, setToccato] = useState(false);
  useEffect(() => {
    if (!toccato) setBrief("");
  }, [tipo, toccato]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [memoria, setMemoria] = useState(false);
  const imparati = useMemoria();

  // Chi arriva davvero a vedere la demo: serve a capire se il problema è lo scroll o la demo.
  useEffect(() => {
    if (sessionStorage.getItem("op_demo_view")) return;
    const box = document.getElementById("demo");
    if (!box) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        sessionStorage.setItem("op_demo_view", "1");
        api.post("/public/funnel-event", { event: "demo_view" }).catch(() => {});
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const money = (n) =>
    new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-US", {
      style: "currency",
      currency: quote?.currency || "USD",
    }).format(Number(n || 0));

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/demo-quote", { brief, lang });
      setQuote(data);
      imparaDa(brief, { correzione: Boolean(quote) });
      if (!sessionStorage.getItem("op_memoria_vista")) {
        sessionStorage.setItem("op_memoria_vista", "1");
        setTimeout(() => setMemoria(true), 1400);
      }
    } catch (err) {
      toast.error(err.response?.status === 429 ? c.limit : apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/public/demo-quote/${quote.id}/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "operativo-sample-quote.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(c.pdfDone);
      setEmail("");
    } catch {
      toast.error(c.pdfError);
    } finally {
      setSending(false);
    }
  };

  // Il dettaglio nel popup arriva dalle parole dell'utente: prima le regole che si dimenticano
  // sempre (sfrido, acconti, percentuali), poi le tariffe.
  const dettaglio = (() => {
    const regole = [
      /\+?\s?\d+\s?%\s?(?:di\s)?[a-zà-ù]+/i,
      /acconto\s+(?:del\s)?\d+\s?%?/i,
      /spese\s+[a-zà-ù]+/i,
      /forfettario/i,
      /\d+\s?€\s?\/?\s?[a-zà-ù]*/i,
    ];
    for (const regola of regole) {
      const trovato = brief.match(regola);
      if (trovato) return `"${trovato[0].trim()}"`;
    }
    return "";
  })();

  return (
    <section className="border-y border-slate-200 bg-slate-50 px-6 py-12 lg:px-16 lg:py-16" id="demo">
      {memoria && (
        <MemoryModal
          detail={dettaglio}
          quoteId={quote?.id}
          onClose={() => setMemoria(false)}
        />
      )}
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          <Sparkles className="h-3 w-3" /> {c.badge}
        </span>
        <h2 className="mt-4 text-xl font-extrabold tracking-tight sm:text-2xl lg:text-3xl">{c.title}</h2>
        <p className="mt-2 hidden text-sm text-slate-600 sm:block md:text-base">{c.sub}</p>
      </div>

      <form onSubmit={generate} className="mt-5 max-w-3xl sm:mt-8" data-testid="demo-form">
        <textarea
          data-testid="demo-brief"
          value={brief}
          onChange={(e) => {
            setToccato(true);
            setBrief(e.target.value);
          }}
          required
          minLength={10}
          maxLength={600}
          rows={3}
          placeholder={c.placeholder}
          className="w-full resize-none rounded-lg border border-slate-300 bg-white p-4 text-sm shadow-sm outline-none transition-colors focus:border-slate-900"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {c.examples.map((example) => (
            <button
              key={example}
              type="button"
              data-testid="demo-example"
              onClick={() => setBrief(example)}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900"
            >
              {example.slice(0, 42)}…
            </button>
          ))}
        </div>
        <Button
          type="submit"
          data-testid="demo-generate"
          disabled={loading || brief.trim().length < 10}
          className="mt-5 gap-2 bg-slate-900 px-6 py-6 text-base text-white hover:bg-slate-800"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? c.generating : c.generate}
        </Button>
      </form>

      {quote && (
        <div className="card-flat mt-10 max-w-3xl overflow-hidden p-0" data-testid="demo-result">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 px-6 py-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.quote}</p>
              <p className="mt-1 text-base font-semibold md:text-lg">{quote.client_name}</p>
            </div>
            <p className="font-mono-num text-2xl font-semibold" data-testid="demo-total">
              {money(quote.total)}
            </p>
          </div>
          <ul className="divide-y divide-slate-100">
            {quote.items.map((item, index) => (
              <li key={index} className="flex items-start justify-between gap-4 px-6 py-3 text-sm">
                <span className="text-slate-700">{item.description}</span>
                <span className="font-mono-num shrink-0 text-slate-500">
                  {item.quantity} × {money(item.unit_price)}
                </span>
              </li>
            ))}
          </ul>
          <div className="space-y-1 border-t border-slate-200 px-6 py-4 text-sm">
            <div className="flex justify-between text-slate-500">
              <span>{c.subtotal}</span>
              <span className="font-mono-num">{money(quote.subtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>
                {c.tax} {quote.tax_rate}%
              </span>
              <span className="font-mono-num">{money(quote.tax_amount)}</span>
            </div>
          </div>

          <form
            onSubmit={downloadPdf}
            className="flex flex-col gap-3 border-t border-slate-200 bg-slate-950 px-6 py-5 sm:flex-row sm:items-center"
            data-testid="demo-pdf-form"
          >
            <p className="text-sm text-slate-300 sm:flex-1">
              {c.pdfPitch} <b className="text-white">{c.pdfBonus}</b>.
            </p>
            <input
              data-testid="demo-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={c.emailPlaceholder}
              className="h-11 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-indigo-500 sm:w-56"
            />
            <Button
              type="submit"
              data-testid="demo-download"
              disabled={sending}
              className="h-11 gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {c.getPdf}
            </Button>
          </form>
          <p className="px-6 py-4 text-xs text-slate-500">
            {c.footer}{" "}
            <a href="/registrati" className="inline-flex items-center gap-1 font-semibold text-slate-900">
              {c.createAccount} <ArrowRight className="h-3 w-3" />
            </a>
          </p>
        </div>
      )}

      {imparati > 0 && (
        <div
          className="mx-auto mt-6 flex max-w-3xl items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-center text-sm font-semibold text-indigo-900"
          data-testid="memoria-banner"
        >
          <BrainCircuit className="h-4 w-4 shrink-0 text-indigo-600" />
          {lang === "it"
            ? `Memoria Operativa: ha imparato ${imparati} ${imparati === 1 ? "dettaglio" : "dettagli"} sul tuo modo di lavorare`
            : `Operational Memory: it has learned ${imparati} ${imparati === 1 ? "detail" : "details"} about the way you work`}
        </div>
      )}
    </section>
  );
};

export default DemoQuote;
